import { create } from 'zustand';

// ─── Domain Models ────────────────────────────────────────────────

export type AnalysisStatus = 'idle' | 'uploading' | 'analyzing' | 'complete' | 'error';

export interface ManualContext {
    matchTitle: string;
    opponentName?: string;
    result?: 'Win' | 'Loss';
    score?: string;
    keyMoments: string; // "Got taken down with a double leg in the 1st..."
    focusArea: string; // "My defense was weak"
}

export interface AnalysisResult {
    technicalFeedback: string[];
    strategicAdjustments: string[];
    recommendedDrills: Array<{
        name: string;
        focus: string;
        reps: string;
    }>;
    strengthAdjustments?: string; // Future: Integration with Strength philosophy
    nutritionTweaks?: string;     // Future: Integration with Nutrition philosophy
}

interface AnalysisState {
    // Input State
    videoFile: File | null;
    videoPreviewUrl: string | null;
    manualContext: ManualContext;

    // Processing State
    status: AnalysisStatus;
    progress: number; // 0-100
    error: string | null;

    // Output State
    result: AnalysisResult | null;

    // Actions
    setVideo: (file: File | null) => void;
    updateContext: (ctx: Partial<ManualContext>) => void;
    startAnalysis: () => void;
    setResult: (result: AnalysisResult) => void;
    setError: (msg: string) => void;
    reset: () => void;
}

// ─── Store Implementation ─────────────────────────────────────────

export const useAnalysisStore = create<AnalysisState>()((set) => ({
    videoFile: null,
    videoPreviewUrl: null,
    manualContext: {
        matchTitle: '',
        keyMoments: '',
        focusArea: ''
    },
    status: 'idle',
    progress: 0,
    error: null,
    result: null,

    setVideo: (file) => {
        if (file) {
            const url = URL.createObjectURL(file);
            set({ videoFile: file, videoPreviewUrl: url, error: null });
        } else {
            set((state) => {
                if (state.videoPreviewUrl) URL.revokeObjectURL(state.videoPreviewUrl);
                return { videoFile: null, videoPreviewUrl: null };
            });
        }
    },

    updateContext: (ctx) => set((state) => ({
        manualContext: { ...state.manualContext, ...ctx }
    })),

    startAnalysis: () => set({ status: 'analyzing', progress: 0, error: null }),

    setResult: (result) => set({ result, status: 'complete', progress: 100 }),

    setError: (msg) => set({ error: msg, status: 'error' }),

    reset: () => set((state) => {
        if (state.videoPreviewUrl) URL.revokeObjectURL(state.videoPreviewUrl);
        return {
            videoFile: null,
            videoPreviewUrl: null,
            manualContext: { matchTitle: '', keyMoments: '', focusArea: '' },
            status: 'idle',
            progress: 0,
            error: null,
            result: null
        };
    })
}));
